import { Injectable } from '@angular/core';
import { map, switchMap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { GetStorefrontGQL } from './queries/get-storefront.query';
import { StorefrontBasic } from './queries/storefront-basic.fragment';
import { GetAllStorefrontsGQL } from './queries/get-all-storefronts.query';
import { GetStorefrontCatalogGQL, StorefrontCatalog } from './queries/get-storefront-catalog';
import { GetStorefrontCategoriesGQL, StorefrontCatalogResponse } from './queries/get-storefront-categories.query';
import { GetFullCatalogGQL, Catalog } from './queries/get-full-catalog.query';
import { UpdateCategoriesGQL, StorefrontCategoryInput } from './queries/update-categories.mutation';
import { CreateCatalogGQL } from './queries/create-catalog.mutation';
import { CreateStorefrontGQL, CreateStorefrontInput } from './queries/create-storefront.mutation';
import { PaymentMethodType } from '@app/shared/models/enums/payment-method-types';
import { Category } from '../models/categories/category';
import { StorefrontLite, GetStorefrontLiteGQL } from './queries/get-storefront-lite.query';
import { ReplaceSubscriptionsGQL } from './queries/replace-subscriptions.mutation';
import { GetReplaceableProductGQL, ReplacableProductResponse } from './queries/get-replaceable-product.query';

@Injectable({
  providedIn: 'root'
})
export class StorefrontService {

  constructor(
    private getStorefrontQuery: GetStorefrontGQL,
    private getStorefrontLiteQuery: GetStorefrontLiteGQL,
    private getAllStorefrontsQuery: GetAllStorefrontsGQL,
    private getProductsQuery: GetStorefrontCatalogGQL,
    private getStorefrontCategoriesQuery: GetStorefrontCategoriesGQL,
    private getFullCatalogQuery: GetFullCatalogGQL,
    private updateCategoriesMutation: UpdateCategoriesGQL,
    private createCatalogMutation: CreateCatalogGQL,
    private createStorefrontMutation: CreateStorefrontGQL,
    private replaceSubscriptionsMutation: ReplaceSubscriptionsGQL,
    private getReplaceableProductQuery: GetReplaceableProductGQL,
  ) { }

  getStorefronts(): Observable<StorefrontBasic[]> {
    return this.getAllStorefrontsQuery.fetch().pipe(
      map(res => res.data.storefronts)
    );
  }

  getStorefront(storefrontId: string): Observable<StorefrontBasic> {
    return this.getStorefrontQuery.fetch({
      storefrontId
    }).pipe(
      map(res => res.data.storefronts[0])
    );
  }

  getStorefrontLite(storefrontId: string): Observable<StorefrontLite> {
    return this.getStorefrontLiteQuery.fetch({storefrontId})
      .pipe(map(res => res.data.storefronts[0]));
  }

  // for use in the manage-category screen
  getStorefrontCategories(storefrontId: string): Observable<StorefrontCatalogResponse> {
    return this.getStorefrontCategoriesQuery.fetch({ storefrontId })
      .pipe(
        map(res => res.data.storefronts[0])
      );
  }

  saveStorefrontCategories(catalogId: string, catalogName: string, categories: StorefrontCategoryInput[]): Observable<Category[]> {
    return this.updateCategoriesMutation.mutate({
      data: {
        catalogId,
        name: catalogName,
        categories
      }
    }).pipe(map(res => res.data.updateCatalog.categories));
  }

  getFullCatalog(storefrontId: string): Observable<Catalog> {
    return this.getFullCatalogQuery.fetch({ storefrontId })
      .pipe(
        map(res => res.data.storefronts[0].catalog)
      );
  }

  getCatalog(storefrontId: string): Observable<StorefrontCatalog> {
    return this.getProductsQuery.fetch({
      storefrontId,
      onlyActive: false,
      onlyWithActiveSubscriptions: false
    }).pipe(
      map(res => res.data.storefronts[0].catalog)
    );
  }

  getInUseCatalog(storefrontId: string): Observable<StorefrontCatalog> {
    return this.getProductsQuery.fetch({
      storefrontId,
      onlyActive: false,
      onlyWithActiveSubscriptions: true
    }).pipe(
      map(res => res.data.storefronts[0].catalog)
    );
  }

  getActiveCatalog(storefrontId: string): Observable<StorefrontCatalog> {
    return this.getProductsQuery.fetch({
      storefrontId,
      onlyActive: true,
      onlyWithActiveSubscriptions: false
    }).pipe(
      map(res => res.data.storefronts[0].catalog)
    );
  }

  create(name: string, partnerId: number): Observable<StorefrontBasic> {
    return this.createCatalogMutation.mutate({ name }).pipe(
      map(res => res.data.createCatalog.catalogId),
      switchMap(catalogId => {
        const data: CreateStorefrontInput = {
          catalogId,
          name,
          partnerId,
          allowedPaymentMethods: null
        };
        return this.createStorefrontMutation.mutate({ data });
      }),
      map(res => res.data.createStorefront)
    );
  }

  replaceSubscriptions(originalProductId: string, replacementProductId: string): Observable<string> {
    return this.replaceSubscriptionsMutation.mutate({
      data: {
        originalProductId: originalProductId,
        replacementProductId: replacementProductId
      }
    }).pipe(
        map(res => res.data.replaceSubscriptions.name)
      );
  }

  getReplaceableProduct(productId: string, storefrontId: string): Observable<ReplacableProductResponse> {
    return this.getReplaceableProductQuery.fetch({productId: productId, storefrontId: storefrontId})
      .pipe(
        map(res => {
          return { product: res.data.product, activeSubscriptionCount: res.data.storefronts[0].subscriptionsSummary.activeCount };
        })
      );
  }
}
